import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

// Cloudflare Turnstile, the human check on every form that sends us data.
//
// ⚠️ ALWAYS ON, unlike the dashboard's (dashboard/src/components/HumanCheck.jsx),
// which only appears after failed sign ins. The website forms post to routes no app
// calls, so the server asks every caller for a token (lib/turnstile requireHuman)
// and a request without one is refused.
//
// ⚠️ INVISIBLE FOR MOST PEOPLE. `interaction-only` keeps the widget hidden unless
// Cloudflare actually needs a tap, so the form looks the same as before for anybody
// it already trusts. The form asks token() on submit, which waits for the check
// to finish rather than failing someone who typed faster than it ran.
//
// ⚠️ Inert without VITE_TURNSTILE_SITE_KEY at build time, and the server is inert
// without TURNSTILE_SECRET_KEY. Ship the site key FIRST: a build without it sends
// no token, so a server holding the secret would refuse every sign up.
//
// Renders nothing in the prerender: the widget mounts in an effect, client only.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''
// How long a submit waits for the check before saying so inline.
const WAIT_MS = 15000

// One script for the page, however many forms ask for it.
let scriptPromise = null
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script')
    el.src = SCRIPT_SRC
    el.async = true
    el.defer = true
    el.onload = () => resolve(window.turnstile)
    el.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed')) }
    document.head.appendChild(el)
  })
  return scriptPromise
}

export function humanCheckConfigured() { return !!SITE_KEY }

// ref API:
//   token()  resolves the current token, or the next one, or '' after WAIT_MS.
//   reset()  asks for a fresh token. ⛔ Call it after EVERY failed submit: a token
//            is single use, so a retry carrying the old one is refused.
const HumanCheck = forwardRef(function HumanCheck(_props, ref) {
  const holder = useRef(null)
  const widgetId = useRef(null)
  const current = useRef('')
  const waiters = useRef([])
  const [failed, setFailed] = useState(false)

  const settle = (t) => {
    current.current = t
    if (!t) return
    waiters.current.splice(0).forEach((w) => w(t))
  }

  useImperativeHandle(ref, () => ({
    token() {
      if (!SITE_KEY) return Promise.resolve('')
      if (current.current) return Promise.resolve(current.current)
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          waiters.current = waiters.current.filter((w) => w !== done)
          resolve('')
        }, WAIT_MS)
        const done = (t) => { clearTimeout(timer); resolve(t) }
        waiters.current.push(done)
      })
    },
    reset() {
      current.current = ''
      try { if (widgetId.current != null) window.turnstile?.reset(widgetId.current) } catch { /* gone */ }
    },
  }), [])

  useEffect(() => {
    if (!SITE_KEY || !holder.current) return undefined
    let cancelled = false
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !ts || !holder.current) return
        widgetId.current = ts.render(holder.current, {
          sitekey: SITE_KEY,
          theme: 'light',
          size: 'flexible',
          appearance: 'interaction-only',
          callback: (t) => { setFailed(false); settle(t) },
          // ⛔ Never leave a stale token in the form: one that has expired is
          // refused server side and reads to the person as the form not working.
          'expired-callback': () => settle(''),
          'error-callback': () => { setFailed(true); settle('') },
        })
      })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => {
      cancelled = true
      try { if (widgetId.current != null) window.turnstile?.remove(widgetId.current) } catch { /* gone */ }
      widgetId.current = null
    }
  }, [])

  if (!SITE_KEY) return null

  return (
    <div>
      <div ref={holder} />
      {failed && (
        // Not "you failed". They have done nothing wrong; our check did not load.
        <p className="mt-2 text-[15px] text-danger">
          The security check didn’t load. Refresh the page to try again.
        </p>
      )}
    </div>
  )
})

export default HumanCheck
